import { largestRemainder } from '@utilities/largest-remainder';
import { useBaseService } from '../base-service';
import { AccountAnalytics, BookingAnalytics, BudgetAnalytics, CategoryAnalytics, InstituteAnalytics, LearningMethods, } from '@models';
import { Currencies, Money } from "ts-money";
const AnalyticsServiceConfig = {
    baseURL: window.location.origin,
    headers: {
        'Content-Type': 'application/json',
    },
};
export const useAnalyticsService = () => {
    //* Parses out the UUID from the url
    let companyUUID = window.location.pathname.split('/')[1];
    const { request } = useBaseService(AnalyticsServiceConfig);
    const getAccountData = async () => {
        const config = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.accounts', {
                company_as_uuid: companyUUID
            }),
        };
        const result = await request(config);
        if (!(result instanceof Error)) {
            const { data } = result;
            return new AccountAnalytics(data.accounts_claimed, data.invites_sent);
        }
    };
    const getBookingsData = async () => {
        const config = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.bookings', {
                company_as_uuid: companyUUID
            }),
        };
        const result = await request(config);
        if (!(result instanceof Error)) {
            const { data } = result;
            return new BookingAnalytics(data.bookings_total);
        }
    };
    const getBudgetData = async () => {
        const budgetTotalAvailableConfig = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.budget.total-available', {
                company_as_uuid: companyUUID
            }),
        };
        const budgetAvailableConfig = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.budget.available', {
                company_as_uuid: companyUUID
            }),
        };
        const budgetTotalAvailablePromise = request(budgetTotalAvailableConfig)
            .catch((error) => error);
        const budgetAvailablePromise = request(budgetAvailableConfig)
            .catch((error) => error);
        const [budgetTotalAvailableResult, budgetAvailableResult] = await Promise.all([
            budgetTotalAvailablePromise,
            budgetAvailablePromise,
        ]);
        if (!(budgetTotalAvailableResult instanceof Error) && !(budgetAvailableResult instanceof Error)) {
            return new BudgetAnalytics(new Money(budgetTotalAvailableResult.data.total_budget_available, Currencies.EUR), new Money(budgetAvailableResult.data.budget_available, Currencies.EUR));
        }
    };
    const getCategoriesData = async () => {
        const config = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.categories', {
                company_as_uuid: companyUUID
            }),
        };
        const result = await request(config);
        if (!(result instanceof Error)) {
            const { data } = result;
            return new CategoryAnalytics(data.top_training_categories);
        }
    };
    const getInstitutesData = async () => {
        const config = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.institutes', {
                company_as_uuid: companyUUID
            }),
        };
        const result = await request(config);
        if (!(result instanceof Error)) {
            const { data } = result;
            return new InstituteAnalytics(data.top_training_institutes);
        }
    };
    const getLearningMethods = async () => {
        const config = {
            method: 'get',
            url: route('nl.employerdashboard.analytics.api.learning-types', {
                company_as_uuid: companyUUID
            }),
        };
        const result = await request(config);
        if (!(result instanceof Error)) {
            const { data } = result;
            const learningMethods = LearningMethods.fromJson(data);
            //* Get all the percentages
            let percentages = learningMethods.methods.map((method) => method.value);
            //* Use the largest remainder method to get the percentages up to 100
            percentages = largestRemainder(100, percentages);
            //* Set the percentages for each method
            learningMethods.methods.forEach(method => {
                method.value = percentages[learningMethods.methods.indexOf(method)];
            });
            return learningMethods;
        }
    };
    return {
        getAccountData,
        getBookingsData,
        getBudgetData,
        getCategoriesData,
        getInstitutesData,
        getLearningMethods,
    };
};
